<template>
  <div class="client-step-wizard">
    <div class="step-header">
      <h3>Sélectionner ou créer un client</h3>
      <p>Choisissez un client existant ou créez-en un nouveau</p>
    </div>

    <div class="client-options">
      <div class="option-tabs">
        <button 
          :class="['tab-btn', { active: activeTab === 'existing' }]"
          @click="activeTab = 'existing'"
        >
          <i class="icon-search"></i>
          Sélectionner un client existant
        </button>
        <button 
          :class="['tab-btn', { active: activeTab === 'new' }]"
          @click="activeTab = 'new'"
        >
          <i class="icon-plus"></i>
          Créer un nouveau client
        </button>
      </div>

      <!-- Existing Client Selection -->
      <div v-if="activeTab === 'existing'" class="tab-content">
        <div class="search-section">
          <div class="search-bar">
            <input 
              v-model="searchQuery"
              type="text"
              placeholder="Rechercher par nom, CIN, ou téléphone..."
              class="search-input"
              @input="debouncedSearch"
            />
            <i class="search-icon">🔍</i>
          </div>

          <div class="filters">
            <select v-model="typeFilter" class="filter-select" @change="searchClients">
              <option value="">Tous les types</option>
              <option value="physique">Physique</option>
              <option value="moral">Moral</option>
            </select>
            <select v-model="nationaliteFilter" class="filter-select" @change="searchClients">
              <option value="">Toutes nationalités</option>
              <option value="marocain">Marocain</option>
              <option value="etranger">Étranger</option>
            </select>
          </div>
        </div>

        <div class="clients-list" v-if="!loading">
          <div v-if="clients.length === 0" class="no-results">
            <p>Aucun client trouvé</p>
            <button @click="activeTab = 'new'" class="btn-create-new">
              Créer un nouveau client
            </button>
          </div>
          
          <div v-else class="client-grid">
            <div 
              v-for="client in clients.filter(c => c && c.id)" 
              :key="client.id"
              :class="['client-card', { selected: selectedClientId === client.id }]"
              @click="selectClient(client)"
            >
              <div class="client-info">
                <div class="client-header">
                  <h4>{{ client.name || client.nom_complet || client.raison_sociale }}</h4>
                  <span class="client-type">{{ (client.tenant_type || client.type) === 'physique' ? 'Physique' : 'Moral' }}</span>
                </div>
                <div class="client-details">
                  <p><strong>{{ (client.tenant_type || client.type) === 'physique' ? 'CIN' : 'RC' }}:</strong> {{ client.identification || client.cin || client.registre_commerce }}</p>
                  <p><strong>Téléphone:</strong> {{ client.phone || client.telephone }}</p>
                  <p><strong>Email:</strong> {{ client.email || 'Non renseigné' }}</p>
                  <p><strong>Adresse:</strong> {{ client.address || client.adresse || 'Non renseignée' }}</p>
                  <p v-if="client.city"><strong>Ville:</strong> {{ client.city }}</p>
                  <p><strong>Nationalité:</strong> {{ (client.nationality || client.nationalite) === 'marocain' ? 'Marocain' : client.country || client.pays || 'Étranger' }}</p>
                </div>
              </div>

            </div>
          </div>

          <div v-if="pagination.total > pagination.per_page" class="pagination">
            <button 
              @click="loadPage(pagination.current_page - 1)"
              :disabled="pagination.current_page <= 1"
              class="pagination-btn"
            >
              Précédent
            </button>
            <span class="pagination-info">
              Page {{ pagination.current_page }} sur {{ Math.ceil(pagination.total / pagination.per_page) }}
            </span>
            <button 
              @click="loadPage(pagination.current_page + 1)"
              :disabled="pagination.current_page >= Math.ceil(pagination.total / pagination.per_page)"
              class="pagination-btn"
            >
              Suivant
            </button>
          </div>
        </div>

        <div v-else class="loading">
          <p>Chargement des clients...</p>
        </div>
      </div>

      <!-- New Client Creation -->
      <div v-if="activeTab === 'new'" class="tab-content">
        <form @submit.prevent="createClient" class="client-form">
          <div class="form-section">
            <h4>Type de client</h4>
            <div class="radio-group">
              <label>
                <input type="radio" v-model="newClient.tenant_type" value="physique" />
                Client Physique
              </label>
              <label>
                <input type="radio" v-model="newClient.tenant_type" value="moral" />
                Client Moral (Entreprise)
              </label>
            </div>
          </div>

          <div class="form-section">
            <h4>Nationalité</h4>
            <div class="radio-group">
              <label>
                <input type="radio" v-model="newClient.nationality" value="marocain" />
                Marocain
              </label>
              <label>
                <input type="radio" v-model="newClient.nationality" value="etranger" />
                Étranger
              </label>
            </div>
          </div>

          <div v-if="newClient.nationality === 'etranger'" class="form-group">
            <label>Pays *</label>
            <select v-model="newClient.country" required>
              <option value="">Sélectionner un pays</option>
              <option value="France">France</option>
              <option value="Espagne">Espagne</option>
              <option value="Allemagne">Allemagne</option>
              <option value="Italie">Italie</option>
              <option value="États-Unis">États-Unis</option>
              <option value="Canada">Canada</option>
              <!-- Add more countries as needed -->
            </select>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>{{ newClient.tenant_type === 'physique' ? 'Nom complet' : 'Raison sociale' }} *</label>
              <input type="text" v-model="newClient.name" required />
            </div>
            <div class="form-group">
              <label>{{ newClient.tenant_type === 'physique' ? 'CIN' : 'Registre de commerce' }} *</label>
              <input type="text" v-model="newClient.identification" required />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Téléphone *</label>
              <input type="tel" v-model="newClient.phone" required />
            </div>
            <div class="form-group">
              <label>Email</label>
              <input type="email" v-model="newClient.email" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Adresse *</label>
              <input type="text" v-model="newClient.address" required />
            </div>
            <div class="form-group">
              <label>Ville *</label>
              <input type="text" v-model="newClient.city" required />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Code postal *</label>
              <input type="text" v-model="newClient.postal_code" required />
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn-primary" :disabled="isCreating">
              {{ isCreating ? 'Création...' : 'Créer le client' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Selected Client Display -->
    <div v-if="selectedClient" class="selected-client">
      <h4>Client sélectionné:</h4>
      <div class="client-summary">
        <strong>{{ selectedClient.name || selectedClient.nom_complet || selectedClient.raison_sociale }}</strong>
        <span>{{ (selectedClient.tenant_type || selectedClient.type) === 'physique' ? 'Physique' : 'Moral' }}</span>
        <span>{{ selectedClient.identification || selectedClient.cin || selectedClient.registre_commerce }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import clientService from '@/services/clientService'

export default {
  name: 'ClientStepWizard',
  props: {
    selectedClient: {
      type: Object,
      default: null
    }
  },
  emits: ['client-selected', 'next'],
  data() {
    return {
      activeTab: 'existing',
      searchQuery: '',
      typeFilter: '',
      nationaliteFilter: '',
      clients: [],
      selectedClientId: null,
      loading: false,
      isCreating: false,
      searchTimeout: null,
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0
      },
      newClient: {
        tenant_type: 'physique',
        nationality: 'marocain',
        name: '',
        identification: '',
        phone: '',
        email: '',
        address: '',
        city: '',
        postal_code: '',
        country: ''
      }
    }
  },
  mounted() {
    this.loadClients()
  },
  methods: {
    async loadClients(page = 1) {
      this.loading = true
      try {
        const filters = { page, per_page: this.pagination.per_page }
        if (this.searchQuery) filters.search = this.searchQuery
        if (this.typeFilter) filters.type = this.typeFilter
        if (this.nationaliteFilter) filters.nationalite = this.nationaliteFilter

        const response = await clientService.getClients(filters)
        
        // Filter out any null or invalid client objects
        this.clients = (response.data || []).filter(client => client && client.id)
        this.pagination = response.pagination || { current_page: 1, per_page: 10, total: 0 }
      } catch (error) {
        console.error('Error loading clients:', error)
        this.clients = []
      } finally {
        this.loading = false
      }
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.searchClients()
      }, 300)
    },

    searchClients() {
      this.loadClients(1)
    },

    loadPage(page) {
      this.loadClients(page)
    },

    selectClient(client) {
      this.selectedClientId = client.id
      this.$emit('client-selected', client)
    },

    async createClient() {
      this.isCreating = true
      try {
        const clientData = { ...this.newClient }
        const response = await clientService.createClient(clientData)
        
        if (response.success) {
          const newClient = response.data
          this.selectClient(newClient)
          this.activeTab = 'existing'
          this.loadClients() // Refresh the list
          
          // Reset form
          this.newClient = {
            tenant_type: 'physique',
            nationality: 'marocain',
            name: '',
            identification: '',
            phone: '',
            email: '',
            address: '',
            city: '',
            postal_code: '',
            country: ''
          }
        }
      } catch (error) {
        console.error('Error creating client:', error)
        alert('Erreur lors de la création du client')
      } finally {
        this.isCreating = false
      }
    }
  }
}
</script>

<style scoped>
.client-step-wizard {
  height: 100%;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: #f8fafc;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.step-header {
  text-align: center;
  margin-bottom: 20px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

.step-header h3 {
  margin: 0 0 8px 0;
  color: #1f2937;
  font-size: 1.5rem;
}

.step-header p {
  margin: 0;
  color: #6b7280;
}

.option-tabs {
  display: flex;
  margin-bottom: 0;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  flex-shrink: 0;
}

.tab-btn {
  flex: 1;
  padding: 16px 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 500;
  color: #6b7280;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.tab-btn.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
}

.tab-content {
  padding: 20px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.search-section {
  margin-bottom: 24px;
}

.search-bar {
  position: relative;
  margin-bottom: 16px;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 48px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 16px;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.filters {
  display: flex;
  gap: 16px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
}

.clients-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 200px;
  width: 100%;
}

.no-results {
  text-align: center;
  padding: 48px 24px;
  color: #6b7280;
}

.btn-create-new {
  margin-top: 16px;
  padding: 12px 24px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.client-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  width: 100%;
  max-width: 100%;
}

.client-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  word-wrap: break-word;
  box-sizing: border-box;
}

.client-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1);
}

.client-card.selected {
  border-color: #3b82f6;
  background: #eff6ff;
}

.client-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.client-header h4 {
  margin: 0;
  color: #1f2937;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

.client-type {
  background: #f3f4f6;
  color: #374151;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
}

.client-details p {
  margin: 4px 0;
  font-size: 0.875rem;
  color: #6b7280;
  overflow: hidden;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
}



.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  margin-top: 24px;
}

.pagination-btn {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 6px;
  cursor: pointer;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 0.875rem;
  color: #6b7280;
}

.loading {
  text-align: center;
  padding: 48px;
  color: #6b7280;
}

.client-form {
  max-width: 600px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.form-section {
  margin-bottom: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  border-left: 4px solid #22305a;
}

.form-section h4 {
  margin: 0 0 12px 0;
  color: #22305a;
  font-size: 1rem;
  font-weight: 600;
}

.radio-group {
  display: flex;
  gap: 24px;
}

.radio-group label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 4px;
  font-weight: 500;
  color: #374151;
}

.form-group input,
.form-group select {
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 16px;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-actions {
  display: flex;
  justify-content: center;
  margin-top: auto;
  padding: 20px 0;
  border-top: 1px solid #e5e7eb;
  background: white;
  position: sticky;
  bottom: 0;
  margin-left: -20px;
  margin-right: -20px;
  padding-left: 20px;
  padding-right: 20px;
}

.btn-primary {
  padding: 12px 32px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.selected-client {
  margin-top: 32px;
  padding: 16px;
  background: #f0f9ff;
  border: 1px solid #0284c7;
  border-radius: 8px;
}

.selected-client h4 {
  margin: 0 0 8px 0;
  color: #0c4a6e;
}

.client-summary {
  display: flex;
  gap: 16px;
  align-items: center;
}

.client-summary span {
  background: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.875rem;
  color: #0c4a6e;
}
.client-options {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

/* Custom scrollbar styling */
.tab-content::-webkit-scrollbar,
.clients-list::-webkit-scrollbar {
  width: 6px;
}

.tab-content::-webkit-scrollbar-track,
.clients-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.tab-content::-webkit-scrollbar-thumb,
.clients-list::-webkit-scrollbar-thumb {
  background: #cbb87c;
  border-radius: 3px;
}

.tab-content::-webkit-scrollbar-thumb:hover,
.clients-list::-webkit-scrollbar-thumb:hover {
  background: #22305a;
}

/* Form section variations */
.form-section:nth-child(2) {
  border-left-color: #cbb87c;
}

.form-section:nth-child(3) {
  border-left-color: #10b981;
}

.form-section:nth-child(4) {
  border-left-color: #f59e0b;
}

/* Better tab button hover states */
.tab-btn:hover {
  background: rgba(203, 184, 124, 0.1);
}

.tab-btn.active {
  color: #22305a !important;
  border-bottom-color: #cbb87c !important;
  background: rgba(203, 184, 124, 0.05);
}

/* Responsive Design */
@media (max-width: 768px) {
  .client-step-wizard {
    max-height: 85vh;
    padding: 16px;
  }
  
  .step-header {
    padding: 16px;
    margin-bottom: 16px;
  }
  
  .tab-content {
    padding: 16px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .radio-group {
    flex-direction: column;
    gap: 12px;
  }
  
  .client-form {
    max-width: 100%;
  }
  
  .form-section {
    margin-bottom: 12px;
    padding: 12px;
  }
  
  .form-actions {
    padding: 16px;
    margin-left: -16px;
    margin-right: -16px;
    padding-left: 16px;
    padding-right: 16px;
  }
}

/* Loading state improvements */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  flex: 1;
}

</style>
